import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ShoppingCartIcon, UserIcon, HeartIcon, GlobeIcon, MessageCircleIcon, PhoneIcon, DollarSignIcon, XIcon, TrendingUpIcon, PlusIcon, LogOutIcon, PackageIcon, ClipboardListIcon, HomeIcon, MapPinIcon, UserPlusIcon, ChevronRightIcon, TrashIcon, ChevronDownIcon, TruckIcon, ShieldCheckIcon, ClockIcon, StethoscopeIcon } from 'lucide-react';
import { AuthModal } from '../modals/AuthModal';
import { LocationModal } from '../modals/LocationModal';
import { MobileDeliverToPill } from './MobileDeliverToPill';
import { OrderTrackingModal } from '../modals/OrderTrackingModal';
import { trackViewCart } from '../../lib/analytics/events';
import { useStore } from '../../lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { DesktopHeader } from './DesktopHeader';
import { POPULAR_SEARCHES, TRENDING_SEARCHES, TRENDING_PRODUCTS } from '../../data/searchData';
interface HeaderProps {
  locationModalOpen: boolean;
  setLocationModalOpen: (open: boolean) => void;
  selectedLocation: {
    name: string;
  } | null;
  setSelectedLocation: (location: {
    name: string;
  } | null) => void;
  disableShrink?: boolean;
  showAnnouncement?: boolean;
}
export function Header({
  locationModalOpen,
  setLocationModalOpen,
  selectedLocation,
  setSelectedLocation,
  disableShrink = false,
  showAnnouncement = false
}: HeaderProps) {
  const navigate = useNavigate();
  const {
    cart: cartItems,
    wishlist: wishlistItems,
    language,
    setLanguage,
    cartCount,
    cartTotal,
    removeFromCart,
    updateQuantity,
    searchQuery,
    setSearchQuery,
    addToCart,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const wishlistCount = wishlistItems.length;
  const [scrolled, setScrolled] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [mobileSearchExpanded, setMobileSearchExpanded] = useState(false);
  const searchInputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showWishlistMenu, setShowWishlistMenu] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const isMobile = windowWidth < 768;
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [orderTrackingModalOpen, setOrderTrackingModalOpen] = useState(false);
  // Track window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);
  // Add click outside listener to close search dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target) && searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  useEffect(() => {
    document.body.style.overflow = mobileSearchExpanded ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileSearchExpanded]);
  const handleSearchInputFocus = () => {
    setShowSearchDropdown(true);
  };
  const handleSearchInputChange = e => {
    setSearchQuery(e.target.value);
    setShowSearchDropdown(true);
  };
  const handleRemoveRecentSearch = search => {
    // Remove by clearing all and re-adding the rest
    const filtered = recentSearches.filter(item => item !== search);
    clearRecentSearches();
    filtered.forEach(s => addRecentSearch(s));
  };
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  // Calculate cart total
  const cartTotalValue = cartTotal();
  const getSearchContent = () => {
    const isSearching = searchQuery.length > 0;
    return <div className="flex flex-col md:flex-row gap-6" data-id="element-2428" data-mp="b05qh3re8tqu">
        {/* Left Column: Suggestions & Categories */}
        <div className="flex-1 space-y-6" data-id="element-2429" data-mp="2voacbvlfl4ns">
          {!isSearching ? <>
              {recentSearches.length > 0 && <div data-id="element-2430" data-mp="2qopczk2cmuaj">
                  <div className="flex items-center justify-between mb-3" data-id="element-2431" data-mp="2qo5vg4gm27x1">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2432" data-mp="2gmv4oqbrfsoy">
                      Recent Searches
                    </h3>
                    <button onClick={() => clearRecentSearches()} className="text-xs text-[#1E2B55] hover:underline font-medium" data-id="element-2433" data-mp="2qjpzoz8trayh">
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-2434" data-mp="2ze4ivcwh75mv">
                    {recentSearches.map((search, index) => <motion.div key={index} initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="flex items-center px-3 py-1.5 rounded-lg bg-gray-50 text-sm hover:bg-gray-100 transition-colors cursor-pointer border border-gray-100 group" onClick={() => setSearchQuery(search)} data-id="element-2435" data-mp="39ah73v994ra8" data-mpi={index}>
                        <ClockIcon className="w-3.5 h-3.5 mr-2 text-gray-400" data-id="element-2436" data-mp="1ltari3amszib" />
                        <span className="text-gray-700" data-id="element-2437" data-mp="1gtoenuyptla1">{search}</span>
                        <button onClick={e => {
                  e.stopPropagation();
                  handleRemoveRecentSearch(search);
                }} className="ml-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2438" data-mp="1gsd9prtja7hc">
                          <XIcon className="w-3 h-3" data-id="element-2439" data-mp="1zl7ho1sqozc2" />
                        </button>
                      </motion.div>)}
                  </div>
                </div>}

              <div data-id="element-2440" data-mp="29jdjykigk7hg">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2441" data-mp="29k6rhnp8r10n">
                  Trending Searches
                </h3>
                <div className="grid grid-cols-2 gap-2" data-id="element-2442" data-mp="2x690ujbj44sa">
                  {TRENDING_SEARCHES.map((search, index) => <button key={index} onClick={() => setSearchQuery(search)} className="flex items-center p-2 rounded-lg hover:bg-blue-50 transition-colors text-sm text-gray-700 group" data-id="element-2443" data-mp="2j4wmgcc0ob8i" data-mpi={index}>
                      <TrendingUpIcon className="w-4 h-4 mr-3 text-blue-500 group-hover:scale-110 transition-transform" data-id="element-2444" data-mp="2e7elb848r0b2" />
                      {search}
                    </button>)}
                </div>
              </div>

              <div data-id="element-2445" data-mp="24hl7e0gmm93n">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2446" data-mp="24eyzu0tqjtlf">
                  Top Categories
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-2447" data-mp="1vgn6hr1hihpa">
                  {trendingCategories.slice(0, 4).map((cat, index) => <button key={index} onClick={() => navigate(`/category/${cat.name.toLowerCase().replace(/ /g, '-')}`)} className="flex items-center gap-3 p-2 rounded-xl border border-gray-100 hover:border-[#1E2B55] hover:bg-blue-50 transition-all group" data-id="element-2448" data-mp="2axlq7d6beb59" data-mpi={index}>
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100" data-id="element-2449" data-mp="1n92e0mk59e7w">
                        <img src={cat.icon} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform" data-id="element-2450" data-mp="1i9g4an3iyg3u" />
                      </div>
                      <span className="text-sm font-medium text-gray-700" data-id="element-2451" data-mp="19b43js8y21xo">
                        {cat.name}
                      </span>
                    </button>)}
                </div>
              </div>
            </> : <div className="space-y-4" data-id="element-2452" data-mp="3kvbongth0a1k">
              <div data-id="element-2453" data-mp="3kyhfb0hqzjze">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2454" data-mp="3pvhk782v2gox">
                  Suggestions
                </h3>
                <div className="space-y-1" data-id="element-2455" data-mp="3tvrqymyh9b4u">
                  {wheyRelatedSearches.filter(s => s.includes(searchQuery.toLowerCase())).slice(0, 5).map((search, index) => <button key={index} onClick={() => setSearchQuery(search)} className="flex items-center w-full p-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700" data-id="element-2456" data-mp="323iu3hyr6grq" data-mpi={index}>
                        <SearchIcon className="w-4 h-4 mr-3 text-gray-400" data-id="element-2457" data-mp="2efswgehs9dn2" />
                        <span className="flex-1 text-left" data-id="element-2458" data-mp="2igunw95gkleh">
                          {search.split(new RegExp(`(${searchQuery})`, 'gi')).map((part, i) => part.toLowerCase() === searchQuery.toLowerCase() ? <span key={i} className="font-bold text-[#1E2B55]" data-id="element-2459" data-mp="29y7gtfo89iqb">
                                  {part}
                                </span> : part)}
                        </span>
                      </button>)}
                </div>
              </div>

              <div data-id="element-2460" data-mp="1vzhnmb4j4378">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2461" data-mp="yiitgn72ahoz">
                  Related Categories
                </h3>
                <div className="grid grid-cols-1 gap-2" data-id="element-2462" data-mp="13fj295vapyjs">
                  {wheyRelatedCategories.slice(0, 3).map((cat, index) => <button key={index} onClick={() => navigate(`/category/${cat.name.toLowerCase().replace(/ /g, '-')}`)} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group" data-id="element-2463" data-mp="qyqd28ag1f0o" data-mpi={index}>
                      <span className="text-sm text-gray-700" data-id="element-2464" data-mp="888p0h0k37ln">{cat.name}</span>
                      <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" data-id="element-2465" data-mp="865ov45ovy37" />
                    </button>)}
                </div>
              </div>
            </div>}
        </div>

        {/* Right Column: Featured Products */}
        <div className="w-full md:w-[320px] border-t md:border-t-0 md:border-l border-gray-100 pt-6 md:pt-0 md:pl-6" data-id="element-2466" data-mp="3qfy385t2a76e">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2467" data-mp="3br69f7qmjeph">
            {isSearching ? 'Matching Products' : 'Trending Products'}
          </h3>
          <div className="space-y-4" data-id="element-2468" data-mp="3fqne3yy887tl">
            {TRENDING_PRODUCTS.map(product => <motion.div key={product.id} initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-xl transition-colors group cursor-pointer" onClick={() => navigate(`/product/${product.id}`)} data-id="element-2469" data-mp="31utg4ayzr2v9">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-100" data-id="element-2470" data-mp="3ppw4khtvv4b7">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-2471" data-mp="h9jhtjj9ksvy" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-2472" data-mp="21tomarjy0d29">
                  <h4 className="text-sm font-bold text-gray-900 line-clamp-2 leading-tight mb-1 group-hover:text-[#1E2B55] transition-colors" data-id="element-2473" data-mp="21swpxeiiubax">
                    {product.name}
                  </h4>
                  <div className="flex items-center gap-2" data-id="element-2474" data-mp="2bjrmpcte8tam">
                    <span className="text-sm font-black text-[#1E2B55]" data-id="element-2475" data-mp="1nslzudp1d0qt">
                      {product.price.toFixed(2)} SAR
                    </span>
                    {product.discount > 0 && <span className="text-[10px] text-gray-400 line-through" data-id="element-2476" data-mp="1wv4yellsgr79">
                        {product.originalPrice.toFixed(2)}
                      </span>}
                  </div>
                </div>
                <button onClick={e => {
              e.stopPropagation();
              haptics.success();
              addToCart({
                id: product.id,
                name: product.name,
                price: product.price,
                image: product.image
              });
              toast.success(`${product.name} added to cart!`);
            }} className="w-8 h-8 flex items-center justify-center bg-blue-50 text-[#1E2B55] rounded-full hover:bg-[#1E2B55] hover:text-white transition-all shadow-sm" data-id="element-2477" data-mp="387e5w91gif64">
                  <PlusIcon className="w-4 h-4" data-id="element-2478" data-mp="qcm0kmpuszzx" />
                </button>
              </motion.div>)}
          </div>

          {isSearching && <button className="w-full mt-6 py-3 text-sm font-bold text-white bg-[#1E2B55] hover:bg-[#162041] rounded-xl transition-all shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2 group" onClick={() => {
          if (searchQuery.trim()) {
            addRecentSearch(searchQuery.trim());
          }
          navigate(`/search?q=${searchQuery}`);
        }} data-id="element-2479" data-mp="v4dp6rpg8vnf">
              <span data-id="element-2480" data-mp="2pjkkr13yrryy">View All Results</span>
              <ChevronRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-2481" data-mp="2piixzzdl5h1e" />
            </button>}
        </div>
      </div>;
  };
  // Handle opening the auth modal
  const openAuthModal = () => {
    setAuthModalOpen(true);
    // Close any other open menus
    setShowAccountMenu(false);
  };
  return <>
      {/* Desktop Header */}
      <DesktopHeader locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={disableShrink} showAnnouncement={showAnnouncement} data-id="element-2482" data-mp="38bmt7p6gc1y4" />

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-[100] m-0 p-0 md:hidden" data-id="element-2483" data-mp="3w1zc1v38jj3h">
        {/* Mobile: Deliver To Pill */}
        <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-2484" data-mp="1pbm4es7q0tq8" />

        {/* Main Header - Mobile */}
        <div className="bg-white shadow-md transition-all duration-300" data-id="element-2485" data-mp="1tf209mbyuq1v">
          <div className="container mx-auto px-4" data-id="element-2486" data-mp="1flkhw4v0njdf">
            <div className="flex items-center justify-between h-14" data-id="element-2487" data-mp="1kkdvrye7tce4">
              <div className="flex items-center" data-id="element-2488" data-mp="1kikpwlbfew76">
                <button onClick={() => navigate('/')} className="mr-4" data-id="element-2489" data-mp="15mlgn7ckyq0g">
                  <img src="https://cdn.magicpatterns.com/uploads/enT9iVMZr5bUJi1w413sX2/Layer_1_(1).png" alt="Dawaak" className="h-9 w-auto" data-id="element-2490" data-mp="1an981u4q18v7" />
                </button>
              </div>

              {/* User Actions - Mobile */}
              <div className="flex items-center space-x-3" data-id="element-2491" data-mp="2r0py8bo2lgfi">
                <button onClick={() => setMobileSearchExpanded(!mobileSearchExpanded)} data-id="element-2492" data-mp="2qz542n5o3zi6">
                  <SearchIcon className="w-6 h-6 text-gray-700" data-id="element-2493" data-mp="363rbkwpf3342" />
                </button>

                <div className="relative" onClick={() => navigate('/cart')} data-id="element-2494" data-mp="3156db2tq6ti9">
                  <ShoppingCartIcon className="w-6 h-6 text-gray-700" data-id="element-2495" data-mp="312tp47xdjmwr" />
                  <span className="absolute -top-1 -right-1 bg-[#1E2B55] text-white text-[10px] font-bold rounded-full flex items-center justify-center w-5 h-5" data-id="element-2496" data-mp="3exw0vbowk6hh">
                    {cartCount()}
                  </span>
                </div>

                <div className="relative" onClick={() => navigate('/account/wishlist')} data-id="element-2497" data-mp="3jveaz5enorr2">
                  <HeartIcon className="w-6 h-6 text-gray-700" data-id="element-2498" data-mp="1ohu5qz7z5bs" />
                  <span className="absolute -top-1 -right-1 bg-[#1E2B55] text-white text-[10px] font-bold rounded-full flex items-center justify-center w-5 h-5" data-id="element-2499" data-mp="3ttv7sj0a00l1">
                    {wishlistCount}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Expandable Search Bar */}
        <div className={`fixed left-0 right-0 bg-white border-b border-gray-200 shadow-md overflow-hidden transition-all duration-300 ease-in-out z-[90] ${mobileSearchExpanded ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}`} style={{
        top: '112px'
      }} data-id="element-2500" data-mp="2o08w0imgut5s">
          <div className="px-4 py-2" data-id="element-2501" data-mp="31r4781kftmcj">
            <div className="relative" data-id="element-2502" data-mp="251wmgvrn7h9o">
              <input type="text" placeholder="Search for medicines..." className="w-full border border-gray-300 rounded-lg py-2.5 pl-4 pr-20 focus:outline-none focus:ring-2 focus:ring-[#1E2B55]" value={searchQuery} onChange={handleSearchInputChange} onFocus={handleSearchInputFocus} data-id="element-2503" data-mp="252y79rygni4l" />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2" data-id="element-2504" data-mp="3pgouuxrjd7s5">
                {searchQuery && <button onClick={handleClearSearch} className="text-gray-400" data-id="element-2505" data-mp="8f453y3tknhp">
                    <XIcon className="w-4 h-4" data-id="element-2506" data-mp="8h85i62if4d8" />
                  </button>}
                <button className="text-gray-500" data-id="element-2507" data-mp="3gssxgq1y0wb">
                  <SearchIcon className="w-5 h-5" data-id="element-2508" data-mp="3uu6sgm0gvgq2" />
                </button>
                <button onClick={() => setMobileSearchExpanded(false)} className="text-gray-500 p-1" data-id="element-2509" data-mp="3qt39hqn4h4km">
                  <XIcon className="w-5 h-5" data-id="element-2510" data-mp="3lvlbgvan89re" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Search Dropdown Overlay */}
        {mobileSearchExpanded && showSearchDropdown && <div className="fixed left-0 right-0 bg-white z-[85] overflow-y-auto shadow-lg" style={{
        top: '160px',
        maxHeight: 'calc(100vh - 160px)'
      }} data-id="element-2511" data-mp="37u8x2ob4sg7m">
            <div className="p-4" data-id="element-2512" data-mp="162mdv8xhdplp">{getSearchContent()}</div>
          </div>}

        {/* Modals */}
        <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} data-id="element-2513" data-mp="38t06mezy2pv7" />
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-2514" data-mp="34trch90zspz4" />
        <OrderTrackingModal isOpen={orderTrackingModalOpen} onClose={() => setOrderTrackingModalOpen(false)} data-id="element-2515" data-mp="2q3bpbop0n7yb" />
      </header>
    </>;
}